import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PopularModel } from '../types';

interface Props {
  onSelectModel: (model: PopularModel) => void;
}

const PopularModelsCarousel: React.FC<Props> = ({ onSelectModel }) => {
  const [models, setModels] = useState<PopularModel[]>([]);
  const [offset, setOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch('/api/popular-models');
        const data = await res.json();
        setModels(data.models || []);
      } catch {
        // silently fail — carousel just won't show
      }
    };
    fetchModels();
  }, []);

  const visibleCount = 4;

  // Build infinite-loop list: visibleCount cyclic clones on each end
  // extended = [...clones from end, ...models, ...clones from start]
  // trackOffset = offset + visibleCount (skip the prepended clone zone)
  const cycleAt = (i: number, len: number) => ((i % len) + len) % len;
  const extended =
    models.length > 0
      ? [
          ...Array.from(
            { length: visibleCount },
            (_, i) => models[cycleAt(i - visibleCount, models.length)]
          ),
          ...models,
          ...Array.from({ length: visibleCount }, (_, i) => models[i % models.length]),
        ]
      : [];

  const trackOffset = offset + visibleCount;

  const advance = useCallback(() => {
    if (models.length <= 1) return;
    setIsTransitioning(true);
    setOffset((prev) => prev + 1);
  }, [models.length]);

  const goBack = useCallback(() => {
    if (models.length <= 1) return;
    setIsTransitioning(true);
    setOffset((prev) => prev - 1);
  }, [models.length]);

  // Auto-advance every 2s
  useEffect(() => {
    if (paused || models.length <= 1) return;
    const id = setInterval(advance, 2000);
    return () => clearInterval(id);
  }, [paused, advance, models.length]);

  // Snap-reset once transition ends to keep offset in [0, models.length)
  useEffect(() => {
    if (models.length === 0) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (offset >= models.length) {
      timer = setTimeout(() => {
        setIsTransitioning(false);
        setOffset((prev) => prev - models.length);
      }, 500);
    } else if (offset < 0) {
      timer = setTimeout(() => {
        setIsTransitioning(false);
        setOffset((prev) => prev + models.length);
      }, 500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [offset, models.length]);

  // Re-enable transitions after snap-reset
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  if (models.length === 0) return null;

  const cardWidthPercent = 100 / visibleCount;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2
          className="text-lg font-bold flex items-center gap-2"
          style={{ color: 'var(--text-primary)' }}
        >
          <Sparkles className="w-5 h-5 text-accent-yellow" />
          Popular Models
        </h2>
        {models.length > 1 && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={goBack}
              className="w-8 h-8 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
            </button>
            <button
              type="button"
              onClick={advance}
              className="w-8 h-8 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
            </button>
          </div>
        )}
      </div>
      <div
        className="overflow-hidden rounded-2xl"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <div
          ref={trackRef}
          className="carousel-track flex"
          style={{
            transform: `translateX(-${trackOffset * cardWidthPercent}%)`,
            transition: isTransitioning ? 'transform 0.5s ease' : 'none',
          }}
        >
          {extended.map((model, i) => (
            <button
              key={`${model.id}-${i}`}
              type="button"
              onClick={() => onSelectModel(model)}
              className="carousel-card flex-shrink-0 p-2"
              style={{ width: `${cardWidthPercent}%` }}
            >
              <div
                className="glass rounded-xl overflow-hidden hover:border-blue-500/50 transition-all hover:scale-[1.03] cursor-pointer group"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <div
                  className="aspect-square overflow-hidden"
                  style={{ backgroundColor: 'var(--surface-inactive)' }}
                >
                  <img
                    src={model.imageUrl}
                    alt={model.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-3">
                  <p
                    className="text-sm font-bold truncate"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {model.name}
                  </p>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-500/20 text-success rounded-lg text-xs font-bold">
                    ${model.price.toFixed(2)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PopularModelsCarousel;
